import { useEffect, useRef, useState } from 'react';
import Dialog, { DialogError } from '../../components/Dialog.jsx';
import { api } from '../../lib/api.js';
import { GROUP_STATUS_LABEL, HOURS, WATCH_AGING, agingDaysValid, buildSettingsPatch, linesOf, localEquivalent, pad } from '../../lib/live-watch-settings.js';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';

const CONFLICT_TEXT = 'Someone else changed these settings. The latest settings are loaded here and your edits were discarded; review them and make your change again.';

function failureText(err) {
  const status = err?.status;
  const message = typeof err?.message === 'string' ? err.message : '';
  if (status === 409 && /changed meanwhile/i.test(message)) return CONFLICT_TEXT;
  if (status === 503) return 'Microsoft Entra did not confirm your verified domains. Nothing was saved; your changes are still here, so try saving again in a few minutes.';
  if (status === 429) return 'Too many changes in the last hour. Try again later.';
  return message || 'The settings could not be saved.';
}

/**
 * What is left of the Watch form: the audiences shared by every live rule, watched groups, guest owners and
 * the digest hour and the aging period of unanswered events. Mount it only while it is open. `onSaved(response)` reloads the page's data; on a version
 * conflict it is called with null.
 */
export default function LiveWatchSettingsDialog({ customerId, watch, onClose, onSaved, returnFocusRef = null }) {
  const s = watch.settings;
  const [draft, setDraft] = useState(() => ({ notifyGuestOwners: s.notifyGuestOwners, digestHourUtc: s.digestHourUtc, agingDays: s.agingDays ?? WATCH_AGING.defaultDays, audiences: { ...s.audiences } }));
  const [groupsText, setGroupsText] = useState(() => s.watchedGroups.map((g) => g.lookup).join('\n'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // After a 409 the draft is stale: the request carries no client version, so another Save would silently overwrite
  // the other admin's change. Save stays off until the reloaded settings arrive and replace the draft.
  const [stale, setStale] = useState(false);
  const staleFrom = useRef(null);
  useEffect(() => {
    if (!stale || watch.settings === staleFrom.current) return;
    const fresh = watch.settings;
    setDraft({ notifyGuestOwners: fresh.notifyGuestOwners, digestHourUtc: fresh.digestHourUtc, agingDays: fresh.agingDays ?? WATCH_AGING.defaultDays, audiences: { ...fresh.audiences } });
    setGroupsText(fresh.watchedGroups.map((g) => g.lookup).join('\n'));
    setStale(false);
    setError(CONFLICT_TEXT);
  }, [stale, watch.settings]);

  const pending = buildSettingsPatch(s, { ...draft, watchedGroups: linesOf(groupsText) });
  const dirty = Object.keys(pending).length > 0;
  const agingOk = agingDaysValid(draft.agingDays);
  useUnsavedChanges(dirty);
  const labels = watch.catalog?.audienceLabels ?? {};

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.patchWatchSettings(customerId, pending);
      await onSaved(r);
      onClose();
    } catch (err) {
      setError(failureText(err));
      if (err?.status === 409) {
        staleFrom.current = s;
        setStale(true);
        try { await onSaved(null); } catch { /* Save stays off until fresh settings arrive; the admin can close and reopen */ }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      wide
      title="Live watch settings"
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy || stale || !dirty || !agingOk} onClick={save}>{busy ? 'Saving...' : 'Save'}</button>
        </>
      )}
    >
      <p className="text-xs text-slate-600">These apply to every live rule. Every change is announced to your guardians before it takes effect. My account notices are a row of the Rules table.</p>
      <fieldset className="space-y-2">
        <legend className="label">Audiences</legend>
        {Object.entries(labels).filter(([k]) => k !== 'self').map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.audiences[k] === true} onChange={(e) => { setError(null); setDraft((d) => ({ ...d, audiences: { ...d.audiences, [k]: e.target.checked } })); }} />
            {label}
          </label>
        ))}
      </fieldset>
      <div className="space-y-2">
        <label className="label" htmlFor="watched-groups">Watched groups (one group id or exact display name per line)</label>
        <textarea id="watched-groups" className="input min-h-[6rem]" value={groupsText} onChange={(e) => { setError(null); setGroupsText(e.target.value); }} />
        {s.watchedGroups.length ? (
          <ul className="text-xs text-slate-600">
            {s.watchedGroups.map((g) => <li key={g.watchId}>{g.lookup}: {g.status === 'valid' ? `found (${g.displayName ?? g.directoryId})` : (GROUP_STATUS_LABEL[g.status] ?? g.status)}</li>)}
          </ul>
        ) : null}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={draft.notifyGuestOwners} onChange={(e) => setDraft((d) => ({ ...d, notifyGuestOwners: e.target.checked }))} />
        Also notify guest owners of groups
      </label>
      <label className="block text-sm">
        <span className="label">Digest hour</span>
        <select className="input w-60" value={draft.digestHourUtc} onChange={(e) => setDraft((d) => ({ ...d, digestHourUtc: Number(e.target.value) }))}>
          {HOURS.map((h) => <option key={h} value={h}>{pad(h)}:00 UTC ({localEquivalent(h)} your time)</option>)}
        </select>
      </label>
      <div className="space-y-1 text-sm">
        <label className="label" htmlFor="aging-days">Close unanswered events after (days)</label>
        <input
          id="aging-days"
          type="number"
          inputMode="numeric"
          min={WATCH_AGING.minDays}
          max={WATCH_AGING.maxDays}
          step={1}
          className="input w-32"
          aria-describedby="aging-days-help"
          aria-invalid={!agingOk}
          value={Number.isNaN(draft.agingDays) ? '' : draft.agingDays}
          onChange={(e) => { setError(null); setDraft((d) => ({ ...d, agingDays: e.target.value === '' ? Number.NaN : Number(e.target.value) })); }}
        />
        <p id="aging-days-help" className="text-xs text-slate-600">
          An event that nobody answers is closed as "Aged out" this many days after it happened, so old questions stop waiting for an answer.
          Aged out is not an answer: it is never counted as expected or confirmed. Flagged events never age out, because their case needs an outcome.
          From {WATCH_AGING.minDays} to {WATCH_AGING.maxDays} days; the default is {WATCH_AGING.defaultDays}.
        </p>
        {agingOk ? null : <p className="text-xs text-ipc-bad" role="alert">Enter a whole number of days from {WATCH_AGING.minDays} to {WATCH_AGING.maxDays}.</p>}
      </div>
      <DialogError message={error} />
    </Dialog>
  );
}
